import React, { useState, useMemo } from "react";
import { DAY_SHEET_TYPES, DAY_SHEET_COLUMNS } from "constants/constants";
import SingleSelect from "widgets/Dropdown/SingleSelect";
import { Textfield, PrimaryButton } from "widgets";
import ErrorButton from "widgets/Buttons/ErrorButton";
import CustomTable from "widgets/Table";
import ClearIcon from "@mui/icons-material/Clear";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Loader from "shared/Loader/Loader";
import {
  useFetchUserTenants,
  useFetchDaySheets,
  useDownloadDaySheet,
  extractVerticalsFromTenants,
} from "store/backend/apiHooks";
import styles from "./DaySheetDownload.module.scss";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import { toDatetimeLocal, toReadableDatetime } from "utils/formatDate";
import { formatCurrency } from "utils/formatCurrency";
import { CurrencyCode } from "constants/enums";
import MultipleSelect from "widgets/Dropdown/MultipleSelect";

const INITIAL_DAY_SHEET_FILTERS = {
  daySheetType: "",
  verticalId: "",
  centerIds: [],
  startDate: "",
  endDate: "",
  applyFilter: false,
};
const FilterField = ({ label, children, className = "" }) => (
  <div className={`${styles.filterField} ${className}`.trim()}>
    <div className={styles.filterLabel}>{label}</div>
    <div className={styles.filterControl}>{children}</div>
  </div>
);

const handleDownload = (
  daySheetId,
  filters,
  downloadDaySheet,
  openSnackbar
) => {
  downloadDaySheet(daySheetId, filters)
    .then((response) => {
      const link = document.createElement("a");
      link.href = response.signedUrl;
      link.click();
    })
    .catch((error) => {
      console.error("Error downloading day sheet", error);
      openSnackbar(error.message || "Error downloading day sheet", "error");
    });
};

const DaySheetDownload = () => {
  const { openSnackbar } = useSnackbarContext();
  const [filters, setFilters] = useState(INITIAL_DAY_SHEET_FILTERS);

  const { data: userTenants = [], isPending } = useFetchUserTenants();
  const {
    data: daySheets = [],
    isLoading: isDaySheetsLoading,
    isError: isDaySheetsError,
    error: daySheetsError,
  } = useFetchDaySheets({ filters });

  const { downloadDaySheet, isLoading: isDownloadDaySheetLoading } =
    useDownloadDaySheet();

  const verticalOptions = useMemo(
    () => extractVerticalsFromTenants(userTenants),
    [userTenants],
  );

  const filteredTenants = useMemo(() => {
    if (!filters.verticalId) return [];
    return userTenants.filter(
      (tenant) => Number(tenant.verticalId) === Number(filters.verticalId),
    );
  }, [userTenants, filters.verticalId]);

  const isDateRangeInvalid = useMemo(() => {
    if (!filters.startDate || !filters.endDate) return false;
    return new Date(filters.endDate) <= new Date(filters.startDate);
  }, [filters.startDate, filters.endDate]);

  const canApplyFilters =
    filters.daySheetType &&
    filters.verticalId &&
    filters.centerIds?.length > 0 &&
    filters.startDate &&
    filters.endDate &&
    !isDateRangeInvalid;

  if (isPending) return <Loader />;

  const updateFilter = (key) => (e) => {
    setFilters((prev) => ({
      ...prev,
      [key]: e.target.value,
      applyFilter: false,
    }));
  };

  const handleVerticalChange = (e) => {
    setFilters((prev) => ({
      ...prev,
      verticalId: e.target.value,
      centerIds: [],
      applyFilter: false,
    }));
  };

  const clearFilters = () => setFilters(INITIAL_DAY_SHEET_FILTERS);

  if (filters.applyFilter && !isDaySheetsLoading && isDaySheetsError) {
    openSnackbar(daySheetsError.message, "error");
  }

  if (filters.applyFilter && !isDaySheetsLoading && daySheets.length === 0) {
    openSnackbar("No day sheets found", "error");
  }

  const renderDownloadActions = (daySheetId) => {
    if (isDownloadDaySheetLoading) {
      return (
        <div className={styles.downloadActions}>
          <PrimaryButton disabled>Downloading...</PrimaryButton>
        </div>
      );
    }

    const showPDF = filters.daySheetType === "OFFLINE";

    return (
      <div className={styles.downloadActions}>
        <PrimaryButton
          onClick={() =>
            handleDownload(
              daySheetId,
              { ...filters, format: "CSV" },
              downloadDaySheet,
              openSnackbar
            )
          }
        >
          Download CSV
        </PrimaryButton>

        {showPDF && (
          <PrimaryButton
            onClick={() =>
              handleDownload(
                daySheetId,
                { ...filters, format: "PDF" },
                downloadDaySheet,
                openSnackbar
              )
            }
          >
            Download PDF
          </PrimaryButton>
        )}
      </div>
    );
  };

  const getRows = (daySheets = []) =>
    daySheets.map((daySheet) => ({
      id: daySheet.id,
      closed_at: toReadableDatetime(toDatetimeLocal(daySheet.close_time)),
      created_by: daySheet.created_by,
      total_amount: formatCurrency(daySheet.total_amount, {
        currencyCode: CurrencyCode.INR,
      }),
      download: renderDownloadActions(daySheet.id),
    }));

  return (
    <>
      <div className={styles.filtersRow}>
        <FilterField label="Type (Offline / Online)">
          <SingleSelect
            placeholder="Select type"
            menuItems={DAY_SHEET_TYPES}
            value={filters.daySheetType}
            handleChange={updateFilter("daySheetType")}
          />
        </FilterField>

        <FilterField label="Vertical" className={styles.verticalField}>
          <SingleSelect
            id="vertical-select"
            placeholder="Select vertical"
            menuItems={verticalOptions}
            value={filters.verticalId}
            handleChange={handleVerticalChange}
          />
        </FilterField>

        <FilterField label="Center" className={styles.centerField}>
          <MultipleSelect
            id="center-select"
            placeholder="Select center"
            value={filters.centerIds}
            menuItems={filteredTenants}
            handleChange={updateFilter("centerIds")}
            disabled={!filters.verticalId}
          />
        </FilterField>

        <FilterField label="(from) Day-Sheet close date">
          <Textfield
            type="datetime-local"
            value={filters.startDate}
            onChange={updateFilter("startDate")}
            error={isDateRangeInvalid}
          />
        </FilterField>

        <FilterField label="(to) Day-Sheet close time">
          <Textfield
            type="datetime-local"
            value={filters.endDate}
            onChange={updateFilter("endDate")}
            error={isDateRangeInvalid}
            helperText={
              isDateRangeInvalid ? "To date must be after From date" : ""
            }
          />
        </FilterField>
      </div>

      <div className={styles.filterButtonsRow}>
        <ErrorButton onClick={clearFilters}>
          <ClearIcon /> Clear Filter
        </ErrorButton>

        <PrimaryButton
          disabled={!canApplyFilters}
          onClick={() => setFilters((prev) => ({ ...prev, applyFilter: true }))}
        >
          <FilterAltIcon /> Apply Filter
        </PrimaryButton>
      </div>

      {isDaySheetsLoading && <Loader />}

      {filters.applyFilter && (
        <div className={styles.table}>
          <CustomTable
            rows={getRows(daySheets, downloadDaySheet)}
            columns={DAY_SHEET_COLUMNS}
            loading={isDaySheetsLoading}
          />
        </div>
      )}
    </>
  );
};

export default DaySheetDownload;
